import React from 'react';
import {
  Box,
  Container,
  Typography,
  Grid,
  Card,
  CardContent,
} from '@mui/material';
import { motion } from 'framer-motion';
import Contact from './Contact';

interface Director {
  name: string;
  title: string;
  qualification: string;
  bio: string;
  image: string | null;
  email: string | null;
  registration?: string;
}

const LegalMigration: React.FC = () => {
  const directors: Director[] = [
    {
      name: 'Felix Chiu',
      title: 'Partner, Legal Services',
      qualification: 'Solicitor',
      bio: `Felix is a qualified solicitor with extensive experience in commercial and corporate law. He has worked with leading law firms in Australia and Asia, providing expert legal advice to businesses and individuals. His practice areas include corporate transactions, commercial contracts, and regulatory compliance.`,
      image: '/images/bio/Felix Chiu bio.jpg',
      email: 'felix.chiu@abarcar.com.au',
      registration: 'Solicitor of the Supreme Court of New South Wales',
    },
    {
      name: 'Juliet Siu',
      title: 'Partner, Migration',
      qualification: 'Registered Migration Agent',
      bio: `Juliet is a registered migration agent with over 15 years of experience in Australian immigration law. She specialises in complex visa applications, including business, skilled, and family visas. Her expertise helps clients navigate the complex Australian migration system with confidence.`,
      image: '/images/bio/juliet-siu.jpg',
      email: 'juliet.siu@abarcar.com.au',
      registration: 'MARN: 1570017',
    },
  ];

  const services = [
    {
      title: 'Comprehensive Legal Advice',
      description: `Our legal team provides expert advice across a wide range of practice areas, including corporate law, commercial contracts, and regulatory compliance. We work closely with clients to understand their needs and deliver practical, cost-effective solutions.`,
    },
    {
      title: 'Business & Commercial Law Support',
      description: `From business formation to complex transactions, we provide comprehensive legal support for businesses of all sizes. Our services include contract drafting and review, corporate governance, and regulatory compliance.`,
    },
    {
      title: 'Family Law & Dispute Resolution',
      description: `We offer sensitive and professional support for family law matters, including divorce, property settlements, and child custody arrangements. Our approach focuses on achieving fair and sustainable outcomes while minimising conflict.`,
    },
    {
      title: 'Visa Services & Australian Migration Advice',
      description: `Our migration team provides expert guidance on all aspects of Australian immigration law. We assist with visa applications, appeals, and compliance matters, helping clients achieve their migration goals with confidence.`,
    },
  ];

  return (
    <Box>
      {/* Top Banner */}
      <Box
        sx={{
          height: '50vh',
          backgroundImage: 'linear-gradient(rgba(0, 0, 0, 0.4), rgba(0, 0, 0, 0.4)), url(/images/generic/bAQH53VquTc.jpg)',
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          textAlign: 'center',
          color: 'white',
        }}
      >
        <Container maxWidth="lg">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            <Typography
              variant="h1"
              sx={{
                mb: 3,
                fontWeight: 700,
                fontSize: { xs: '2.5rem', md: '4rem' },
                textShadow: '2px 2px 4px rgba(0, 0, 0, 0.6)',
              }}
            >
              Abarcar Legal & Migration
            </Typography>
          </motion.div>
        </Container>
      </Box>

      {/* Meet Our Directors Section */}
      <Box sx={{ py: { xs: 6, md: 12 }, backgroundColor: 'background.default' }}>
        <Container maxWidth="lg">
          <Typography
            variant="h3"
            align="center"
            sx={{
              mb: 6,
              fontWeight: 600,
              fontSize: { xs: '1.75rem', md: '2rem' },
            }}
          >
            Meet Our Directors
          </Typography>
          <Grid container spacing={4}>
            {directors.map((director, index) => (
              <Grid item xs={12} md={6} key={index}>
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5, delay: index * 0.2 }}
                >
                  <Card
                    sx={{
                      height: { xs: 'auto', md: 750 },
                      display: 'flex',
                      flexDirection: 'column',
                      backgroundColor: 'background.paper',
                      borderRadius: 2,
                      boxShadow: '0 4px 20px rgba(0, 0, 0, 0.05)',
                    }}
                  >
                    <Box
                      sx={{
                        display: 'flex',
                        justifyContent: 'center',
                        mt: 3,
                        mb: 2,
                      }}
                    >
                      {director.image && (
                        <Box
                          component="img"
                          src={director.image}
                          alt={director.name}
                          sx={{
                            width: 225,
                            height: 225,
                            borderRadius: '50%',
                            objectFit: 'cover',
                          }}
                        />
                      )}
                    </Box>
                    <CardContent
                      sx={{
                        flexGrow: 1,
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        textAlign: 'center',
                        pt: 0,
                        pb: 3,
                      }}
                    >
                      <Typography
                        variant="h5"
                        sx={{
                          mb: 1,
                          fontWeight: 600,
                          color: 'primary.main',
                        }}
                      >
                        {director.name}
                      </Typography>
                      <Typography
                        variant="subtitle1"
                        sx={{
                          mb: 1,
                          color: 'secondary.main',
                          fontWeight: 500,
                        }}
                      >
                        {director.title}
                      </Typography>
                      <Typography
                        variant="body2"
                        sx={{
                          mb: 2,
                          color: 'text.secondary',
                        }}
                      >
                        {director.qualification === 'CPA' ? 'CPA Australia' : director.qualification}
                        {director.registration && ` (${director.registration})`}
                      </Typography>
                      <Typography
                        variant="body1"
                        sx={{
                          color: 'text.secondary',
                          lineHeight: 1.7,
                          // flexGrow: 1,
                          // display: 'flex',
                          alignItems: 'center',
                          maxHeight: '600px',
                        }}
                      >
                        {director.bio}
                      </Typography>
                      {director.email && (
                      <Typography
                        variant="body2"
                        sx={{
                          mt: 5,
                          color: 'primary.main',
                        }}
                      >
                        {director.email}
                      </Typography>
                    )}
                    </CardContent>
                  </Card>
                </motion.div>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* Our Legal and Migration Services Section */}
      <Box sx={{ py: { xs: 6, md: 12 }, backgroundColor: 'background.paper' }}>
        <Container maxWidth="lg">
          <Typography
            variant="h3"
            align="center"
            sx={{
              mb: 6,
              fontWeight: 600,
              fontSize: { xs: '1.75rem', md: '2rem' },
            }}
          >
            Our Legal and Migration Services
          </Typography>
          <Grid container spacing={4}>
            {services.map((service, index) => (
              <Grid item xs={12} md={6} key={index}>
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5, delay: index * 0.2 }}
                >
                  <Card
                    sx={{
                      height: '100%',
                      display: 'flex',
                      flexDirection: 'column',
                      backgroundColor: 'background.paper',
                      borderRadius: 2,
                      boxShadow: '0 4px 20px rgba(0, 0, 0, 0.05)',
                    }}
                  >
                    <CardContent>
                      <Typography
                        variant="h5"
                        sx={{
                          mb: 2,
                          fontWeight: 600,
                          color: 'primary.main',
                        }}
                      >
                        {service.title}
                      </Typography>
                      <Typography
                        variant="body1"
                        sx={{
                          color: 'text.secondary',
                          lineHeight: 1.7,
                        }}
                      >
                        {service.description}
                      </Typography>
                    </CardContent>
                  </Card>
                </motion.div>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* Contact Banner */}
      <Box
        sx={{
          height: '40vh',
          backgroundImage: 'linear-gradient(rgba(0, 0, 0, 0.4), rgba(0, 0, 0, 0.4)), url(/images/generic/PhYq704ffdA.jpg)',
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          textAlign: 'center',
          color: 'white',
        }}
      >
        <Container maxWidth="lg">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            <Typography
              variant="h2"
              sx={{
                mb: 3,
                fontWeight: 700,
                fontSize: { xs: '2rem', md: '3rem' },
                textShadow: '2px 2px 4px rgba(0, 0, 0, 0.6)',
              }}
            >
              CONTACT US FOR AN OBLIGATION FREE DISCUSSION
            </Typography>
          </motion.div>
        </Container>
      </Box>

      {/* Get in Touch Section */}
      <Contact />
    </Box>
  );
};

export default LegalMigration; 